import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  CloseOutlined as RemoveIcon,
  UploadFileOutlined as UploadIcon,
  ViewListOutlined as ViewIcon,
} from '@mui/icons-material';
import ScheduleService, {
  DAY_LABEL, SCHEDULE_DAYS, ScheduleDay, ScheduleDriver, ScheduleOverview, ScheduleWorkArea, Worksheet, WorkSchedule, driverOn,
} from '../../api/services/schedule.service';
import { describeError } from '../../api/services/payroll.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import { ChoiceFilter, FilterBar } from '../../components/ui/FilterBar';
import { LoadingState } from '../../components/ui/States';
import { useAuthStore } from '../../store/authStore';
import { shortDate } from '../payroll/payrollUI';
import SchedulePanel from './SchedulePanel';
import {
  addAssignedDays, clearDay, mergeWorkAreas, setDayDriver, setUsualDriver, shortName, workAreasOf,
} from './scheduleMath';

// The work areas (FedEx's WA#, the routes) and who runs them.
//
// WAs change rarely, so this is a settings-like page: enter them once, or
// upload a Weekly Service Worksheet and let it fill them in — FedEx's name
// for each WA, the days it ran, and who ran it. Each WA has a usual driver
// for every day it is dispatched; any one day can go to somebody else.
// The same assignments read two ways: by WA (who runs this route) and by
// driver (which route each person runs).

type View = 'wa' | 'driver';
const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

const fromOverview = (o: ScheduleOverview): WorkSchedule => {
  const plan = o.plan;
  return {
    // Only what is saved: someone new keeps counting at the default and
    // working the days their WAs give them until Usual week sets them.
    drivers: (plan?.drivers || []).filter((d) => d.saved).map((d) => ({
      accountId: d.accountId, name: d.name, role: d.role, saved: d.saved, workArea: d.workArea || '', maxStops: d.maxStops, maxPackages: d.maxPackages, days: d.days,
    })),
    workAreas: plan && plan.workAreas.length ? plan.workAreas : o.suggestions.workAreas,
    growthPercent: plan ? plan.growthPercent : o.defaultGrowthPercent,
    defaultMaxStops: plan?.defaultMaxStops ?? null,
    defaultMaxPackages: plan?.defaultMaxPackages ?? null,
  };
};

/** One WA's day: who runs it, as a button that opens the choice. */
const AssignCell: React.FC<{
  w: ScheduleWorkArea;
  day: ScheduleDay;
  people: ScheduleDriver[];
  nameOf: (id: string | null | undefined) => string | null;
  disabled: boolean;
  onChange: (w: ScheduleWorkArea) => void;
}> = ({
  w, day, people, nameOf, disabled, onChange,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const on = w.days.includes(day);
  const who = on ? driverOn(w, day) : null;
  const exception = on && (w.dayDrivers || []).some((x) => x.day === day);
  const name = nameOf(who);
  const pick = (next: ScheduleWorkArea) => { onChange(next); setAnchor(null); };
  const label = !on ? 'Not dispatched' : name ? `${name}${exception ? ' (not the usual driver)' : ''}` : 'Nobody yet';
  return (
    <>
      <Tooltip title={`WA ${w.number}, ${DAY_LABEL[day]}: ${label}`} disableInteractive>
        <Box
          component="button"
          type="button"
          disabled={disabled}
          aria-label={`WA ${w.number}, ${DAY_LABEL[day]}: ${label}`}
          onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
          sx={{
            width: '100%', height: 28, px: 0.5, border: 0, borderRadius: 1.5, font: 'inherit', fontSize: 12.5,
            cursor: disabled ? 'default' : 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontWeight: exception ? 700 : 500,
            color: !on ? 'text.disabled' : !who ? 'warning.dark' : exception ? 'secondary.dark' : 'primary.dark',
            bgcolor: (t) => (!on ? 'transparent'
              : !who ? alpha(t.palette.warning.main, 0.16)
                : exception ? alpha(t.palette.secondary.main, 0.16) : alpha(t.palette.primary.main, 0.14)),
            '&:hover:not(:disabled)': { outline: '1px solid', outlineColor: 'divider' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
          }}
        >
          {!on ? '·' : name ? shortName(name) : 'Nobody'}
        </Box>
      </Tooltip>
      <Menu open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} slotProps={{ paper: { sx: { maxHeight: 420, minWidth: 240 } } }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 0.5 }}>WA {w.number}, {DAY_LABEL[day]}</Typography>
        <MenuItem dense selected={on && !exception} onClick={() => pick(setDayDriver(w, day, 'usual'))}>
          <ListItemIcon>{on && !exception && <CheckIcon fontSize="small" />}</ListItemIcon>
          <ListItemText primary={w.driverId ? `Usual driver, ${nameOf(w.driverId)}` : 'Usual driver (none set)'} />
        </MenuItem>
        <MenuItem dense selected={on && exception && !who} onClick={() => pick(setDayDriver(w, day, null))}>
          <ListItemIcon>{on && exception && !who && <CheckIcon fontSize="small" />}</ListItemIcon>
          <ListItemText primary="Nobody yet" secondary="A floater covers" />
        </MenuItem>
        <MenuItem dense selected={!on} onClick={() => pick(clearDay(w, day))}>
          <ListItemIcon>{!on && <CheckIcon fontSize="small" />}</ListItemIcon>
          <ListItemText primary="Not dispatched" />
        </MenuItem>
        <Divider />
        {people.filter((p) => p.accountId !== w.driverId).map((p) => (
          <MenuItem key={p.accountId} dense selected={on && exception && who === p.accountId} onClick={() => pick(setDayDriver(w, day, p.accountId))}>
            <ListItemIcon>{on && exception && who === p.accountId && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText primary={p.name} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const WorkAreas: React.FC = () => {
  const { canArea } = useAuthStore();
  const editable = canArea('operations', 'edit');
  const [overview, setOverview] = useState<ScheduleOverview | null>(null);
  const [plan, setPlan] = useState<WorkSchedule | null>(null);
  const [worksheet, setWorksheet] = useState<Worksheet | null>(null);
  const [view, setView] = useState<View>('wa');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newArea, setNewArea] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async (keepEdits = false) => {
    try {
      const body = await ScheduleService.overview();
      setOverview(body);
      if (!keepEdits) { setPlan(fromOverview(body)); setDirty(false); }
      setWorksheet(body.worksheet ? await ScheduleService.worksheet(body.worksheet.weekEnd) : null);
    } catch (err) {
      setError(describeError(err, 'Could not load the work areas.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Everyone who can be put on a WA: today's Drivers and BCs.
  const people = useMemo(() => (overview?.plan?.drivers || overview?.suggestions.drivers || [])
    .slice().sort((a, b) => a.name.localeCompare(b.name)), [overview]);
  const nameOf = useCallback((id: string | null | undefined) => (id ? people.find((p) => p.accountId === id)?.name || 'Someone who left' : null), [people]);

  const change = (next: WorkSchedule) => { setPlan(next); setDirty(true); setNotice(null); };
  const setArea = (number: string, next: ScheduleWorkArea) => {
    if (plan) change({ ...plan, workAreas: plan.workAreas.map((w) => (w.number === number ? next : w)) });
  };
  const addArea = () => {
    const number = newArea.trim();
    if (!plan || !number) return;
    if (!plan.workAreas.some((w) => w.number === number)) {
      change({
        ...plan,
        workAreas: [...plan.workAreas, {
          number, name: '', days: ['mon', 'tue', 'wed', 'thu', 'fri'], driverId: null, dayDrivers: [],
        }],
      });
    }
    setNewArea('');
  };

  const save = async () => {
    if (!plan) return;
    setBusy(true);
    try {
      const { drivers, added } = addAssignedDays(plan.drivers, plan.workAreas);
      await ScheduleService.savePlan({ ...plan, drivers });
      await load();
      setNotice(added.length
        ? `Saved. ${added.map((a) => `${a.name} now works ${DAY_LABEL[a.day]}`).join(', ')}, to run the WA they were put on.`
        : 'Saved.');
      setError(null);
    } catch (err) {
      setError(describeError(err, 'The work areas were not saved.'));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const sheet = await ScheduleService.uploadWorksheet(await file.text(), file.name);
      const body = await ScheduleService.overview();
      setOverview(body);
      setWorksheet(sheet);
      // Fill what is empty from the worksheet; what was set by hand stays.
      setPlan((p) => (p ? { ...p, workAreas: mergeWorkAreas(p.workAreas, body.suggestions.workAreas) } : fromOverview(body)));
      setDirty(true);
      const open = sheet.names.filter((n) => !n.accountId).length;
      setNotice(`Read the worksheet for ${shortDate(sheet.weekStart)} – ${shortDate(sheet.weekEnd)}: ${new Set(sheet.days.map((d) => d.workArea)).size} work areas, ${sheet.names.length} people${open ? `, ${open} to place below` : ''}. Check the work areas, then save.`);
    } catch (err) {
      setError(describeError(err, 'That worksheet could not be read.'));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const place = async (key: string, accountId: string | null) => {
    if (!worksheet) return;
    try {
      const sheet = await ScheduleService.matchWorksheetName(key, accountId, worksheet.weekEnd);
      if (sheet) setWorksheet(sheet);
      const body = await ScheduleService.overview();
      setOverview(body);
      if (plan) change({ ...plan, workAreas: mergeWorkAreas(plan.workAreas, body.suggestions.workAreas) });
    } catch (err) {
      setError(describeError(err, 'That was not saved.'));
    }
  };

  const unplaced = (worksheet?.names || []).filter((n) => !n.accountId);
  const dispatched = (day: ScheduleDay) => plan?.workAreas.filter((w) => w.days.includes(day)).length ?? 0;
  const uncovered = (day: ScheduleDay) => plan?.workAreas.filter((w) => w.days.includes(day) && !driverOn(w, day)).length ?? 0;
  const dayCols = SCHEDULE_DAYS.map((d) => <col key={d} style={{ width: 96 }} />);
  const fresh = overview && !overview.plan?.workAreas.length;

  return (
    <PageBody fluid>
      <SchedulePanel />
      <PageHeader
        title="Work areas"
        subtitle="Each WA, the days FedEx dispatches it, and who runs it."
        actions={editable && (
          <>
            <input ref={fileInput} type="file" accept=".csv,text/csv" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }} />
            <Tooltip title="FedEx's Weekly Service Worksheet (CSV). Fills in the WAs, their names and who ran them.">
              <span>
                <Button size="small" startIcon={<UploadIcon />} disabled={busy} onClick={() => fileInput.current?.click()}>Upload worksheet</Button>
              </span>
            </Tooltip>
            <Button variant="contained" size="small" disabled={busy || !plan || (!dirty && !fresh)} onClick={save}>{busy ? 'Saving…' : 'Save'}</Button>
          </>
        )}
      >
        <FilterBar onReset={() => setView('wa')} canReset={view !== 'wa'}>
          <ChoiceFilter<View>
            icon={<ViewIcon />}
            label="View"
            value={view}
            defaultValue="wa"
            onChange={setView}
            choices={[{ key: 'wa', label: 'By work area' }, { key: 'driver', label: 'By driver' }]}
          />
        </FilterBar>
      </PageHeader>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {fresh && plan && plan.workAreas.length > 0 && !notice && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {overview?.suggestions.workAreasFrom === 'worksheet' && overview.worksheet
            ? `These come from the worksheet for ${shortDate(overview.worksheet.weekStart)} – ${shortDate(overview.worksheet.weekEnd)}. Check them, then save.`
            : 'These come from the routes synced from FedEx. Name them, put a driver on each, then save. A Weekly Service Worksheet fills in who ran them.'}
        </Alert>
      )}

      {unplaced.length > 0 && (
        <Card sx={{ mb: 2, p: 1.5 }}>
          <Typography variant="subtitle2">Who are these?</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Names on the worksheet that match nobody, or more than one person. Answered once, remembered for every week.
          </Typography>
          <Stack gap={1}>
            {unplaced.map((n) => (
              <Stack key={n.key} direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                <Typography variant="body2" sx={{ minWidth: 220, fontWeight: 600 }}>{n.name}</Typography>
                <TextField
                  select size="small" value="" label="Is" sx={{ minWidth: 240 }} disabled={!editable}
                  onChange={(e) => place(n.key, e.target.value)}
                >
                  {[...n.candidates.map((c) => c.accountId), ...people.map((p) => p.accountId).filter((id) => !n.candidates.some((c) => c.accountId === id))]
                    .map((id) => <MenuItem key={id} value={id}>{nameOf(id)}{n.candidates.some((c) => c.accountId === id) ? ' (likely)' : ''}</MenuItem>)}
                </TextField>
              </Stack>
            ))}
          </Stack>
        </Card>
      )}

      {!plan ? (error ? null : <LoadingState />) : view === 'wa' ? (
        <Stack gap={2}>
          <Card sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 1080, tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: 70 }} />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 200 }} />
                  {dayCols}
                  <col style={{ width: 40 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ px: 1.25 }}>WA</TableCell>
                    <TableCell sx={{ px: 1 }}>Name</TableCell>
                    <TableCell sx={{ px: 1 }}>Usual driver</TableCell>
                    {SCHEDULE_DAYS.map((d) => <TableCell key={d} align="center" sx={{ px: 0.5 }}>{DAY_LABEL[d]}</TableCell>)}
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.workAreas.map((w) => (
                    <TableRow key={w.number} hover>
                      <TableCell sx={{ ...num, py: 0.25, px: 1.25, fontWeight: 700 }}>{w.number}</TableCell>
                      <TableCell sx={{ py: 0.25, px: 0.5 }}>
                        <TextField
                          size="small" value={w.name || ''} placeholder="Name" disabled={!editable} fullWidth
                          sx={{ '& input': { py: 0.5, px: 0.75 } }} inputProps={{ 'aria-label': `WA ${w.number} name`, maxLength: 60 }}
                          onChange={(e) => setArea(w.number, { ...w, name: e.target.value })}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.25, px: 0.5 }}>
                        <TextField
                          select size="small" value={w.driverId || ''} disabled={!editable} fullWidth
                          sx={{ '& .MuiSelect-select': { py: 0.5, px: 0.75 } }} inputProps={{ 'aria-label': `WA ${w.number} usual driver` }}
                          onChange={(e) => setArea(w.number, setUsualDriver(w, e.target.value || null))}
                        >
                          <MenuItem value=""><em>Nobody</em></MenuItem>
                          {people.map((p) => <MenuItem key={p.accountId} value={p.accountId}>{p.name}</MenuItem>)}
                        </TextField>
                      </TableCell>
                      {SCHEDULE_DAYS.map((day) => (
                        <TableCell key={day} sx={{ py: 0.25, px: 0.5 }}>
                          <AssignCell w={w} day={day} people={people} nameOf={nameOf} disabled={!editable} onChange={(next) => setArea(w.number, next)} />
                        </TableCell>
                      ))}
                      <TableCell sx={{ py: 0, px: 0.25 }}>
                        {editable && (
                          <IconButton size="small" aria-label={`Remove WA ${w.number}`} onClick={() => change({ ...plan, workAreas: plan.workAreas.filter((x) => x.number !== w.number) })}>
                            <RemoveIcon fontSize="inherit" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} sx={{ px: 1.25 }}>
                      {editable && (
                        <Stack direction="row" gap={1} alignItems="center">
                          <TextField
                            size="small" placeholder="New WA #" value={newArea} sx={{ width: 120, '& input': { py: 0.5 } }}
                            inputProps={{ 'aria-label': 'New WA' }}
                            onChange={(e) => setNewArea(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') addArea(); }}
                          />
                          <Button size="small" startIcon={<AddIcon />} onClick={addArea} disabled={!newArea.trim()}>Add WA</Button>
                        </Stack>
                      )}
                    </TableCell>
                    {SCHEDULE_DAYS.map((d) => (
                      <TableCell key={d} align="center" sx={{ ...num, px: 0.5 }}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>{dispatched(d)} WAs</Typography>
                        {uncovered(d) > 0 && <Typography variant="caption" color="warning.dark" sx={{ display: 'block' }}>{uncovered(d)} open</Typography>}
                      </TableCell>
                    ))}
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Card sx={{ p: 1.5, maxWidth: 760 }}>
            <Typography variant="subtitle2">A new driver&apos;s capacity</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
              What someone counts at until their own numbers are set in Usual week. Left empty, it is the middle of your drivers&apos; numbers
              {plan && overview?.plan?.defaults ? ` (now ${overview.plan.defaults.maxStops} stops, ${overview.plan.defaults.maxPackages} packages)` : ''}.
            </Typography>
            <Stack direction="row" gap={1.5}>
              {(['defaultMaxStops', 'defaultMaxPackages'] as const).map((k) => (
                <TextField
                  key={k} size="small" type="number" disabled={!editable} sx={{ width: 160 }}
                  label={k === 'defaultMaxStops' ? 'Stops a day' : 'Packages a day'}
                  value={plan[k] ?? ''}
                  placeholder={String((k === 'defaultMaxStops' ? overview?.plan?.defaults?.maxStops : overview?.plan?.defaults?.maxPackages) ?? '')}
                  inputProps={{ min: 0 }}
                  onChange={(e) => change({ ...plan, [k]: e.target.value === '' ? null : Math.max(0, Math.round(Number(e.target.value))) })}
                />
              ))}
            </Stack>
          </Card>
        </Stack>
      ) : (
        <Card sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 860, tableLayout: 'fixed' }}>
              <colgroup>
                <col />
                <col style={{ width: 90 }} />
                {SCHEDULE_DAYS.map((d) => <col key={d} style={{ width: 80 }} />)}
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ px: 1.25 }}>Driver</TableCell>
                  <TableCell sx={{ px: 1 }}><Tooltip title="The WA they are the usual driver of"><span>Usual WA</span></Tooltip></TableCell>
                  {SCHEDULE_DAYS.map((d) => <TableCell key={d} align="center" sx={{ px: 0.5 }}>{DAY_LABEL[d]}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {people.map((p) => {
                  const usual = plan.workAreas.find((w) => w.driverId === p.accountId)?.number || '';
                  const days = plan.drivers.find((d) => d.accountId === p.accountId)?.days || p.days;
                  return (
                    <TableRow key={p.accountId} hover>
                      <TableCell sx={{ py: 0.5, px: 1.25 }}>
                        {p.name}
                        {p.role === 'BC' && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>BC</Typography>}
                      </TableCell>
                      <TableCell sx={{ ...num, py: 0.5, px: 1, fontWeight: 700 }}>{usual || <Typography component="span" variant="caption" color="text.secondary">Floater</Typography>}</TableCell>
                      {SCHEDULE_DAYS.map((day) => {
                        const was = workAreasOf(plan.workAreas, p.accountId, day);
                        const free = !was.length && days.includes(day);
                        return (
                          <TableCell key={day} align="center" sx={{ ...num, py: 0.5, px: 0.5 }}>
                            {was.length ? (
                              <Box component="span" sx={{ fontWeight: was.some((n) => n !== usual) ? 700 : 500, color: was.some((n) => n !== usual) ? 'secondary.dark' : 'primary.dark' }}>
                                {was.join(', ')}
                              </Box>
                            ) : free ? (
                              <Tooltip title="Scheduled, no WA of their own: covers whichever is short"><Typography component="span" variant="caption" color="text.secondary">Flex</Typography></Tooltip>
                            ) : <Typography component="span" color="text.disabled">·</Typography>}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </PageBody>
  );
};

export default WorkAreas;
